<script lang="ts">
import type { Data, Frame, Layout, PlotlyHTMLElement } from "plotly.js-dist-min";

export interface PlotlyWidgetSpec {
  data: Data[];
  layout: Partial<Layout>;
  frames: Array<Partial<Frame>>;
}
</script>

<script setup lang="ts">
import { addFrames, purge, react, relayout } from "plotly.js-dist-min";
import { onBeforeUnmount, onMounted, ref, watch } from "vue";

import { isDeepEqual } from "@/services/utils";

const { spec } = defineProps<{
  spec: PlotlyWidgetSpec;
}>();
const emit = defineEmits<{ dataChange: [data: Data[]] }>();

const container = ref<HTMLDivElement>();

function makeLayout(): Partial<Layout> {
  const width = container.value?.clientWidth;
  let height = container.value?.clientHeight;
  const parent = container.value?.parentElement;
  if (parent) {
    const styles = getComputedStyle(parent);
    const top = parseFloat(styles.getPropertyValue("padding-top"));
    const bottom = parseFloat(styles.getPropertyValue("padding-bottom"));
    height = Math.round(parent.clientHeight - top - bottom);
  }
  return {
    ...spec.layout,
    paper_bgcolor: "transparent",
    width,
    height,
    font: {
      family: "GeistMono, monospace",
    },
  };
}

function onPlotRestyle() {
  if (_plot !== null) {
    emit("dataChange", _plot.data);
  }
}

function removePlotListeners() {
  _plot?.removeAllListeners("plotly_restyle");
  _plot = null;
}

let _plot: PlotlyHTMLElement | null = null;
let resizeObserver: ResizeObserver | null = null;

async function buildPlot(containerValue: HTMLDivElement, plotData: any) {
  removePlotListeners();
  _plot = await react(containerValue, plotData, makeLayout(), {
    responsive: true,
  });
  if (spec.frames) {
    addFrames(_plot, spec.frames);
  }
  _plot.on("plotly_restyle", onPlotRestyle);
}

function handleResize() {
  if (container.value) {
    relayout(container.value, makeLayout());
  }
}

onMounted(() => {
  if (container.value) {
    // Observe both the container and its parent since makeLayout() uses parent dimensions
    resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(container.value);

    const parent = container.value.parentElement;
    if (parent) {
      resizeObserver.observe(parent);
    }

    buildPlot(container.value, spec.data);
  }
});

onBeforeUnmount(() => {
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }
  if (container.value) {
    removePlotListeners();
    purge(container.value);
  }
});

watch(
  () => spec.data,
  async (newData, oldData) => {
    if (!isDeepEqual(newData, oldData)) {
      if (container.value) {
        await buildPlot(container.value, newData);
      }
    }
  }
);
</script>

<template>
  <div class="plotly-widget" ref="container"></div>
</template>

<style scoped>
.plotly-widget {
  width: 100%;
  max-height: 100%;
}
</style>
